// C5-revive / C5-polish — interpret an EXTRACTED targeting payload for display.
//
// Extracted targeting is persisted VERBATIM (TargetingConfig.rawPayload) in one of
// two shapes:
//   - Meta-native snake_case (external ads): geo_locations{.places/.cities/
//     .custom_locations} / age_min / genders / flexible_spec / device_platforms /
//     brand_safety_content_filter_levels / targeting_automation / excluded_* …
//   - Zernio camelCase (Zernio-created): countries / ageMin / gender / cities …
//
// PURE + shape-agnostic. Produces (a) a uniform summary of what kyck CAN render,
// and (b) a rich list of ADVANCED Meta fields kyck doesn't render but that still
// apply at publish. Never mutates the payload — the raw is authoritative.

export type InterpretedCity = {
  name: string;
  radius: number | null;
  distanceUnit: 'kilometer' | 'mile' | null;
  country: string | null;
};

export type InterpretedTargeting = {
  countries: string[];
  ageMin: number | null;
  ageMax: number | null;
  gender: 'all' | 'male' | 'female';
  cities: InterpretedCity[];
  regions: { name: string }[];
  interests: { name: string }[];
};

export type TargetingShape = 'meta-native' | 'zernio' | 'unknown';

// One advanced (kyck-unrenderable but preserved) field, ready to render "label: detail".
export type AdvancedTargetingItem = { label: string; detail: string };

const isObj = (v: unknown): v is Record<string, any> => typeof v === 'object' && v !== null;
const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : []);
const asNumber = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const nameOf = (v: any): string => (typeof v?.name === 'string' ? v.name : typeof v === 'string' ? v : '');

function nonEmpty(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  if (isObj(v)) return Object.keys(v).length > 0;
  return v != null && v !== '';
}

export function detectTargetingShape(raw: unknown): TargetingShape {
  if (!isObj(raw)) return 'unknown';
  const metaSignals = ['geo_locations', 'age_min', 'age_max', 'genders', 'flexible_spec'];
  const zernioSignals = ['countries', 'ageMin', 'ageMax', 'cities', 'regions', 'interests'];
  if (metaSignals.some((k) => k in raw)) return 'meta-native';
  if (typeof raw.gender === 'string' || zernioSignals.some((k) => k in raw)) return 'zernio';
  return 'unknown';
}

// Meta genders: [1] male, [2] female, [1,2]/absent → all.
function genderFromMeta(genders: unknown): 'all' | 'male' | 'female' {
  const g = asArray(genders);
  if (g.length === 1 && g[0] === 1) return 'male';
  if (g.length === 1 && g[0] === 2) return 'female';
  return 'all';
}

function toCity(c: any): InterpretedCity {
  const unit = c?.distanceUnit ?? c?.distance_unit;
  return {
    name: nameOf(c),
    radius: asNumber(c?.radius),
    distanceUnit: unit === 'mile' ? 'mile' : unit ? 'kilometer' : null,
    country: typeof c?.country === 'string' ? c.country : null,
  };
}

export function interpretExtractedTargeting(raw: unknown): InterpretedTargeting {
  if (!isObj(raw)) {
    return { countries: [], ageMin: null, ageMax: null, gender: 'all', cities: [], regions: [], interests: [] };
  }
  const geo = isObj(raw.geo_locations) ? raw.geo_locations : {};

  // Countries: union of every place a country code can live, deduped + sorted.
  const countrySet = new Set<string>();
  const addCountries = (arr: unknown) =>
    asArray(arr).forEach((c) => typeof c === 'string' && countrySet.add(c));
  const addCountryFromObjs = (arr: unknown) =>
    asArray(arr).forEach((o) => typeof o?.country === 'string' && countrySet.add(o.country));
  addCountries(raw.countries);
  addCountries(geo.countries);
  addCountryFromObjs(geo.places);
  addCountryFromObjs(geo.cities);
  addCountryFromObjs(geo.custom_locations);
  const countries = [...countrySet].sort();

  // Cities: Zernio top-level `cities`, Meta `geo_locations.cities`, AND Meta
  // `geo_locations.places` (verified business locations) — all rendered as cities.
  const cities = [...asArray(raw.cities), ...asArray(geo.cities), ...asArray(geo.places)]
    .map(toCity)
    .filter((c) => c.name !== '');

  const regions = [...asArray(raw.regions), ...asArray(geo.regions)]
    .map((r) => ({ name: nameOf(r) }))
    .filter((r) => r.name !== '');

  // Interests: Zernio top-level `interests` + Meta flexible_spec[*].interests[].
  const flexInterests = asArray(raw.flexible_spec).flatMap((spec) => asArray(spec?.interests));
  const interests = [...asArray(raw.interests), ...flexInterests]
    .map((i) => ({ name: nameOf(i) }))
    .filter((i) => i.name !== '');

  const gender =
    raw.gender === 'male' || raw.gender === 'female'
      ? raw.gender
      : 'genders' in raw
        ? genderFromMeta(raw.genders)
        : 'all';

  return {
    countries,
    ageMin: asNumber(raw.ageMin ?? raw.age_min),
    ageMax: asNumber(raw.ageMax ?? raw.age_max),
    gender,
    cities,
    regions,
    interests,
  };
}

// "N (a, b, c +K more)" — count + up to `max` names.
function namesSummary(arr: unknown, max = 3): string {
  const names = asArray(arr).map((x) => nameOf(x)).filter(Boolean);
  const shown = names.slice(0, max).join(', ');
  const more = names.length > max ? ` +${names.length - max} more` : '';
  return `${asArray(arr).length}${shown ? ` (${shown}${more})` : ''}`;
}

const DEFAULT_LOCATION_TYPES = ['home', 'recent'];
function isDefaultLocationTypes(lt: any[]): boolean {
  return lt.length === 0 || (lt.length === 2 && lt.includes('home') && lt.includes('recent'));
}

// Rich descriptions of advanced fields kyck preserves but doesn't render. Each is
// surfaced only when MEANINGFULLY present (non-default). Order is stable.
export function describeAdvancedTargeting(raw: unknown): AdvancedTargetingItem[] {
  if (!isObj(raw)) return [];
  const out: AdvancedTargetingItem[] = [];
  const geo = isObj(raw.geo_locations) ? raw.geo_locations : {};

  if (nonEmpty(raw.excluded_custom_audiences)) {
    out.push({ label: 'Excluded custom audiences', detail: namesSummary(raw.excluded_custom_audiences) });
  }
  if (nonEmpty(raw.excluded_user_lists)) {
    out.push({ label: 'Excluded user lists', detail: namesSummary(raw.excluded_user_lists) });
  }

  const exZones = isObj(raw.excluded_geo_locations) ? asArray(raw.excluded_geo_locations.custom_locations) : [];
  if (exZones.length > 0) {
    const r = asNumber(exZones[0]?.radius);
    const unit = exZones[0]?.distance_unit === 'mile' ? 'mi' : 'km';
    out.push({ label: 'Excluded geo zones', detail: `${exZones.length} zones${r !== null ? ` with ${r}${unit} radius` : ''}` });
  }

  if (asArray(geo.custom_locations).length > 0) {
    out.push({ label: 'Custom locations', detail: `${asArray(geo.custom_locations).length}` });
  }

  const bs = asArray(raw.brand_safety_content_filter_levels).filter((x) => typeof x === 'string');
  if (bs.length > 0 && !(bs.length === 1 && bs[0] === 'FACEBOOK_STANDARD')) {
    out.push({ label: 'Brand safety', detail: bs.join(', ') });
  }

  if (isObj(raw.targeting_automation) && raw.targeting_automation.advantage_audience === 1) {
    out.push({ label: 'Advantage+ audience', detail: 'enabled' });
  }

  if (asArray(raw.flexible_spec).length > 1) {
    out.push({ label: 'Combined audience', detail: `${asArray(raw.flexible_spec).length} specs (AND)` });
  }

  const lt = asArray(geo.location_types);
  if (lt.length > 0 && !isDefaultLocationTypes(lt)) {
    out.push({ label: 'Location types', detail: lt.join(', ') });
  }

  // Device platforms: only "advanced" when narrowed off a main surface (mobile or
  // desktop dropped). [mobile, desktop] (± connected_tv) is the everyday default.
  const dp = asArray(raw.device_platforms).filter((x) => typeof x === 'string');
  if (dp.length > 0 && !(dp.includes('mobile') && dp.includes('desktop'))) {
    out.push({ label: 'Device platforms', detail: dp.join(', ') });
  }

  return out;
}

// ---------- Feature C: richer preview model ----------
//
// The flat InterpretedTargeting above merges Meta `geo_locations.places` into
// `cities` and surfaces excluded/advanced geo only as terse advanced lines. The
// preview model below keeps the FULL geo picture — every geo TYPE separately,
// named, with radius/unit, and EXCLUDED locations named under their own group —
// so an AM can verify exactly what they're about to publish or save. Pure +
// shape-agnostic (Meta-native snake_case OR Zernio/kyck camelCase), like the rest
// of this module. Names prefer `.name`, then `.key`/string, so a key-only entry
// still renders rather than vanishing.

export type PreviewGeoEntry = {
  name: string; // .name, else .key / string, else '' (filtered out)
  radius: number | null;
  distanceUnit: 'kilometer' | 'mile' | null;
};

export type PreviewCustomLocation = {
  label: string | null; // .name / .address_string when Meta supplies one
  latitude: number | null;
  longitude: number | null;
  radius: number | null;
  distanceUnit: 'kilometer' | 'mile' | null;
};

export type PreviewGeoGroup = {
  cities: PreviewGeoEntry[];
  regions: PreviewGeoEntry[];
  neighborhoods: PreviewGeoEntry[];
  places: PreviewGeoEntry[];
  customLocations: PreviewCustomLocation[];
};

export type TargetingPreviewModel = {
  countries: string[];
  included: PreviewGeoGroup;
  excluded: PreviewGeoGroup;
  ageMin: number | null;
  ageMax: number | null;
  gender: 'all' | 'male' | 'female';
  interests: { name: string }[];
  locationTypes: string[]; // home / recent / travel_in …
  advantageAudience: boolean | null; // null when the payload doesn't carry the flag
};

const toUnit = (v: unknown): 'kilometer' | 'mile' | null =>
  v === 'mile' ? 'mile' : v ? 'kilometer' : null;

function toGeoEntry(e: any): PreviewGeoEntry {
  const name =
    typeof e?.name === 'string' && e.name !== ''
      ? e.name
      : typeof e === 'string'
        ? e
        : e?.key != null
          ? String(e.key)
          : '';
  return { name, radius: asNumber(e?.radius), distanceUnit: toUnit(e?.distanceUnit ?? e?.distance_unit) };
}

function toCustomLocation(e: any): PreviewCustomLocation {
  const label =
    typeof e?.name === 'string' && e.name !== ''
      ? e.name
      : typeof e?.address_string === 'string' && e.address_string !== ''
        ? e.address_string
        : null;
  return {
    label,
    latitude: asNumber(e?.latitude),
    longitude: asNumber(e?.longitude),
    radius: asNumber(e?.radius),
    distanceUnit: toUnit(e?.distanceUnit ?? e?.distance_unit),
  };
}

const emptyGeoGroup = (): PreviewGeoGroup => ({
  cities: [],
  regions: [],
  neighborhoods: [],
  places: [],
  customLocations: [],
});

// Build a geo group from a Meta `geo_locations`-shaped container, optionally
// folding in the Zernio/kyck top-level `cities`/`regions` (one shape is always
// empty, so the union covers both). `places`/`neighborhoods`/`custom_locations`
// are Meta-only. Named entries only (key-or-name fallback applied first).
function buildGeoGroup(
  container: Record<string, any>,
  topLevel: { cities?: unknown; regions?: unknown } = {},
): PreviewGeoGroup {
  const named = (arr: unknown) => asArray(arr).map(toGeoEntry).filter((e) => e.name !== '');
  return {
    cities: named([...asArray(topLevel.cities), ...asArray(container.cities)]),
    regions: named([...asArray(topLevel.regions), ...asArray(container.regions)]),
    neighborhoods: named(container.neighborhoods),
    places: named(container.places),
    customLocations: asArray(container.custom_locations).map(toCustomLocation),
  };
}

function buildLocationTypes(raw: Record<string, any>): string[] {
  const geo = isObj(raw.geo_locations) ? raw.geo_locations : {};
  const meta = asArray(geo.location_types).filter((x) => typeof x === 'string');
  if (meta.length > 0) return meta;
  const lt = raw.locationTypes; // kyck single-value shape
  if (lt === 'home_recent') return ['home', 'recent'];
  if (typeof lt === 'string' && lt !== '') return [lt];
  return [];
}

function buildAdvantageAudience(raw: Record<string, any>): boolean | null {
  const ta = raw.targeting_automation;
  if (isObj(ta) && 'advantage_audience' in ta) return ta.advantage_audience === 1 || ta.advantage_audience === true;
  return null;
}

// The richer preview (Feature C). Reuses interpretExtractedTargeting for the
// already-correct countries / age / gender / interests derivation, then adds the
// per-type included + excluded geo groups, location types, and advantage-audience.
export function buildTargetingPreview(raw: unknown): TargetingPreviewModel {
  const base = interpretExtractedTargeting(raw);
  if (!isObj(raw)) {
    return {
      countries: base.countries,
      included: emptyGeoGroup(),
      excluded: emptyGeoGroup(),
      ageMin: base.ageMin,
      ageMax: base.ageMax,
      gender: base.gender,
      interests: base.interests,
      locationTypes: [],
      advantageAudience: null,
    };
  }
  const geo = isObj(raw.geo_locations) ? raw.geo_locations : {};
  const exGeo = isObj(raw.excluded_geo_locations) ? raw.excluded_geo_locations : {};
  return {
    countries: base.countries,
    included: buildGeoGroup(geo, { cities: raw.cities, regions: raw.regions }),
    excluded: buildGeoGroup(exGeo),
    ageMin: base.ageMin,
    ageMax: base.ageMax,
    gender: base.gender,
    interests: base.interests,
    locationTypes: buildLocationTypes(raw),
    advantageAudience: buildAdvantageAudience(raw),
  };
}

// True when the payload carries ANY audience signal (so the UI can say "no
// targeting found on this ad" rather than show empty defaults). Shape-agnostic.
export function hasTargetingSignal(raw: unknown): boolean {
  if (!isObj(raw)) return false;
  const i = interpretExtractedTargeting(raw);
  return (
    i.countries.length > 0 ||
    i.cities.length > 0 ||
    i.regions.length > 0 ||
    i.interests.length > 0 ||
    i.ageMin !== null ||
    i.gender !== 'all' ||
    describeAdvancedTargeting(raw).length > 0
  );
}
